import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { WaitlistEntry, WaitlistNotification } from 'types/waitlist';

export type WaitlistOffer = {
  gameId: number;
  gameType: WaitlistEntry['gameType'];
  message: string;
  receivedAt: number;
};

export type WaitlistState = {
  version: number;
  playerGuid: string;
  updatedAt: number;
  entryCount: number;
  entriesByGameId: Record<number, WaitlistEntry[]>;
  mySpotByGameId: Record<number, WaitlistEntry>;
  pendingOffer: WaitlistOffer | null;
  // gameId of a seat offer the player just accepted, awaiting its buy-in flow.
  // The offer prompt is app-level, but the buy-in modal lives inside each
  // table's own provider tree (single-table route or a grid cell, each with
  // its own GlobalProvider). Routing the "open buy-in" intent through redux
  // lets the correct table's GameInner open its OWN buy-in modal, which a flag
  // set on the app-level GlobalContext could never reach in grid view.
  acceptedOfferGameId: number | null;
};

const initialState: WaitlistState = {
  version: 0,
  playerGuid: '',
  updatedAt: 0,
  entryCount: 0,
  entriesByGameId: {},
  mySpotByGameId: {},
  pendingOffer: null,
  acceptedOfferGameId: null
};

type ReplaceEntriesPayload = {
  playerGuid: string;
  entries: WaitlistEntry[];
  version?: number;
  updatedAt?: number;
};

const hasValidOffer = (entry: WaitlistEntry | undefined): entry is WaitlistEntry =>
  Boolean(entry && entry.yourTurn && entry.availableSeats > 0);

const countEntries = (state: WaitlistState): number =>
  Object.values(state.entriesByGameId).reduce((total, entries) => total + entries.length, 0);

const updateProjectionMeta = (
  state: WaitlistState,
  {
    playerGuid,
    entryCount,
    version,
    updatedAt
  }: {
    playerGuid?: string;
    entryCount?: number;
    version?: number;
    updatedAt?: number;
  } = {}
) => {
  if (playerGuid !== undefined) state.playerGuid = playerGuid;
  state.version = version ?? state.version + 1;
  state.updatedAt = updatedAt ?? Date.now();
  state.entryCount = entryCount ?? countEntries(state);
};

const syncPendingOfferWithMySpots = (state: WaitlistState) => {
  const pendingSpot = state.pendingOffer
    ? state.mySpotByGameId[state.pendingOffer.gameId]
    : undefined;

  if (hasValidOffer(pendingSpot)) {
    return;
  }

  const nextOfferSpot = Object.values(state.mySpotByGameId).find(hasValidOffer);
  if (!nextOfferSpot) {
    state.pendingOffer = null;
    return;
  }

  state.pendingOffer = {
    gameId: nextOfferSpot.gameId,
    gameType: nextOfferSpot.gameType,
    message: 'JOIN_IT',
    receivedAt: Date.now()
  };
};

const waitlistSlice = createSlice({
  name: 'waitlist',
  initialState,
  reducers: {
    replaceWaitlistEntries: (state, action: PayloadAction<ReplaceEntriesPayload>) => {
      const { playerGuid, entries, version, updatedAt } = action.payload;
      // A periodic waitlist broadcast can omit the auto buy-in the player set
      // locally (it is persisted server-side via PATCH /api/waitlist but not
      // always echoed back in the broadcast payload). Keep the prior value so
      // reopening the Set Buy-In dialog still shows what they chose — otherwise
      // the next broadcast silently resets it to 0.
      const prevMySpot = state.mySpotByGameId;
      state.entriesByGameId = {};
      state.mySpotByGameId = {};
      for (const entry of entries) {
        if (!state.entriesByGameId[entry.gameId]) {
          state.entriesByGameId[entry.gameId] = [];
        }
        if (entry.playerGuid === playerGuid) {
          const prior = prevMySpot[entry.gameId];
          const mySpot =
            prior && prior.autoBuyIn > 0 && !(entry.autoBuyIn > 0)
              ? { ...entry, autoBuyIn: prior.autoBuyIn }
              : entry;
          state.mySpotByGameId[entry.gameId] = mySpot;
          state.entriesByGameId[entry.gameId].push(mySpot);
        } else {
          state.entriesByGameId[entry.gameId].push(entry);
        }
      }
      updateProjectionMeta(state, {
        playerGuid,
        entryCount: entries.length,
        version,
        updatedAt
      });
      syncPendingOfferWithMySpots(state);
    },
    upsertWaitlistEntry: (
      state,
      action: PayloadAction<{ playerGuid: string; entry: WaitlistEntry }>
    ) => {
      const { playerGuid, entry } = action.payload;
      const bucket = state.entriesByGameId[entry.gameId] ?? [];
      const idx = bucket.findIndex((e) => e.id === entry.id);
      if (idx >= 0) {
        bucket[idx] = entry;
      } else {
        bucket.push(entry);
      }
      state.entriesByGameId[entry.gameId] = bucket;
      if (entry.playerGuid === playerGuid) {
        state.mySpotByGameId[entry.gameId] = entry;
      }
      updateProjectionMeta(state, { playerGuid });
      syncPendingOfferWithMySpots(state);
    },
    removeWaitlistEntry: (
      state,
      action: PayloadAction<{ gameId: number; playerGuid: string; myGuid: string }>
    ) => {
      const { gameId, playerGuid, myGuid } = action.payload;
      const bucket = state.entriesByGameId[gameId];
      if (bucket) {
        state.entriesByGameId[gameId] = bucket.filter((e) => e.playerGuid !== playerGuid);
        if (state.entriesByGameId[gameId].length === 0) {
          delete state.entriesByGameId[gameId];
        }
      }
      if (playerGuid === myGuid) {
        delete state.mySpotByGameId[gameId];
      }
      updateProjectionMeta(state, { playerGuid: myGuid });
      syncPendingOfferWithMySpots(state);
    },
    clearWaitlistForGame: (state, action: PayloadAction<{ gameId: number }>) => {
      delete state.entriesByGameId[action.payload.gameId];
      delete state.mySpotByGameId[action.payload.gameId];
      updateProjectionMeta(state);
      syncPendingOfferWithMySpots(state);
    },
    setWaitlistOffer: (state, action: PayloadAction<WaitlistNotification | null>) => {
      if (!action.payload) {
        state.pendingOffer = null;
        return;
      }
      state.pendingOffer = {
        gameId: action.payload.gameId,
        gameType: action.payload.gameType,
        message: action.payload.message,
        receivedAt: Date.now()
      };
    },
    clearWaitlistOffer: (state) => {
      state.pendingOffer = null;
    },
    // Player accepted a seat offer: flag the table so its own GameInner opens
    // the buy-in modal in the right provider scope. The pending offer is left
    // as-is — it must persist until the player is actually seated (the engine
    // keeps re-broadcasting my turn). Nothing renders a prompt from it: the
    // JOIN chip (TableHeader) and lobby card (YourTables) render from
    // `mySpotByGameId`. MultiTableGrid still reads `pendingOffer.gameId` to
    // focus the offered table in grid view. `acceptedOfferGameId` silences the
    // alert sound only until GameInner opens the buy-in and clears it (Game.tsx);
    // after that the hook's `opportunityKey` latch stops a repeat initial chime.
    acceptSeatOffer: (state, action: PayloadAction<number>) => {
      state.acceptedOfferGameId = action.payload;
    },
    clearAcceptedSeatOffer: (state) => {
      state.acceptedOfferGameId = null;
    },
    resetWaitlist: () => initialState
  }
});

export const {
  replaceWaitlistEntries,
  upsertWaitlistEntry,
  removeWaitlistEntry,
  clearWaitlistForGame,
  setWaitlistOffer,
  clearWaitlistOffer,
  acceptSeatOffer,
  clearAcceptedSeatOffer,
  resetWaitlist
} = waitlistSlice.actions;

export default waitlistSlice.reducer;

export type { WaitlistState as WaitlistReducerState };
